import React, { useRef, useEffect, useState } from 'react';

const BlanketGhost = () => {
  const canvasRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const layersRef = useRef([]);
  const globalVars = useRef({
    bx: 0, by: 0,
    x2: 0, y2: 0, r2: 0, dy2: 0, dx2: 0, tailr2: 0, l2: 0,
    eye: 0
  });

  const numLayers = 35;
  const size = 50;
  const speed = 1;
  const PI = Math.PI;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Define Layer class
    class Layer {
      constructor(i, canvasWidth, canvasHeight) {
        const g = globalVars.current;

        this.L = 1 - i / numLayers;
        this.R = (30 * Math.pow(this.L, 3) - 58.5 * Math.pow(this.L, 2) + 28 * this.L + 0.92) * size / 2;
        this.dY = (10 * Math.pow(this.L, 3) - 10 * Math.pow(this.L, 2)) * size;
        this.gray = 220 - 100 * this.L;
        this.first = i === numLayers - 1 ? 1 : 0;
        this.dX = 0;
        this.tailR = 0;

        if (this.L > 0.775) {
          this.dX = (22 * Math.pow(this.L, 2) - 30 * this.L + 10) * size;
          if (i === 0) {
            this.tailR = 0;
            this.R = 0;
          } else {
            this.tailR = (-86.7 * Math.pow(this.L, 2) + 153.2 * this.L - 66) * size / 2;
          }
        }

        this.R2 = g.r2;
        this.dY2 = g.dy2;
        this.dX2 = g.dx2;
        this.tailR2 = g.tailr2;
        this.L2 = g.l2;

        g.r2 = this.R;
        g.dy2 = this.dY;
        g.dx2 = this.dX;
        g.tailr2 = this.tailR;
        g.l2 = this.L;

        this.X = canvasWidth / 2;
        this.Y = canvasHeight / 2;
        this.BX = this.X;
        this.BY = this.Y;
        this.X2 = this.X;
        this.Y2 = this.Y;
      }

      updateLayer(targetX, targetY) {
        const g = globalVars.current;

        this.BX = g.bx;
        this.BY = g.by;
        g.bx = this.X;
        g.by = this.Y;

        if (this.first === 1) {
          this.X = (speed / numLayers * targetX + this.X) / (speed / numLayers + 1);
          this.Y = (speed / numLayers * targetY + this.Y) / (speed / numLayers + 1);
        } else {
          this.X = this.BX;
          this.Y = this.BY;
        }

        this.X2 = g.x2;
        this.Y2 = g.y2;
        g.x2 = this.X;
        g.y2 = this.Y;
      }

      drawLayer(ctx) {
        const g = globalVars.current;

        ctx.fillStyle = `rgb(${this.gray}, ${this.gray}, ${this.gray})`;
        ctx.strokeStyle = `rgb(${this.gray}, ${this.gray}, ${this.gray})`;
        ctx.lineWidth = 0;

        if (this.L <= 0.775) {
          // Draw body
          ctx.beginPath();
          ctx.arc(this.X, this.Y - this.dY, this.R, 0, 2 * PI);
          ctx.fill();

          // Draw fins
          if (this.L > 0.3 && this.L <= 0.4) {
            ctx.save();
            ctx.translate(this.X, this.Y);
            ctx.rotate(PI / 4);

            const a1 = -this.R;
            const b = this.R * 0.6;
            const dist1 = Math.sqrt(a1 * a1 + b * b);
            const angle1 = Math.atan2(b, a1);

            ctx.beginPath();
            ctx.ellipse(dist1 * Math.cos(angle1), dist1 * Math.sin(angle1),
              size * (this.L - 0.3) * 15, size * (this.L - 0.3) * 40, 0, 0, 2 * PI);
            ctx.fill();

            const a2 = this.R;
            const dist2 = Math.sqrt(a2 * a2 + b * b);
            const angle2 = Math.atan2(b, a2);

            ctx.beginPath();
            ctx.ellipse(dist2 * Math.cos(angle2), dist2 * Math.sin(angle2),
              size * (this.L - 0.3) * 40, size * (this.L - 0.3) * 15, 0, 0, 2 * PI);
            ctx.fill();

            ctx.restore();
          }

          // Draw belly
          ctx.fillStyle = `rgb(${this.gray + 25}, ${this.gray + 25}, ${this.gray + 25})`;
          ctx.strokeStyle = `rgb(${this.gray + 25}, ${this.gray + 25}, ${this.gray + 25})`;
          ctx.lineWidth = 2;
          ctx.beginPath();

          if (this.first !== 1) {
            ctx.moveTo(this.X2 + Math.cos(PI / 6) * this.R2, this.Y2 - this.dY2 + Math.sin(PI / 6) * this.R2);
          }

          for (let i = PI / 6; i <= 5 * PI / 6; i += PI / 15) {
            ctx.lineTo(this.X + Math.cos(i) * this.R, this.Y - this.dY + Math.sin(i) * this.R);
          }

          if (this.first !== 1) {
            ctx.lineTo(this.X2 + Math.cos(5 * PI / 6) * this.R2, this.Y2 - this.dY2 + Math.sin(5 * PI / 6) * this.R2);
          }

          ctx.lineTo(this.X, this.Y - this.dY + this.R / 3);
          ctx.closePath();
          ctx.fill();

          // Draw blowhole
          if (this.L > 0.4 && this.L < 0.5) {
            ctx.fillStyle = `rgb(${this.gray - 50}, ${this.gray - 50}, ${this.gray - 50})`;
            ctx.strokeStyle = `rgb(${this.gray - 50}, ${this.gray - 50}, ${this.gray - 50})`;
            ctx.beginPath();

            const startAngle = (17 + Math.abs(this.L - 0.45) / 0.05) * PI / 12;
            const endAngle = (19 - Math.abs(this.L - 0.45) / 0.05) * PI / 12;

            ctx.moveTo(this.X2 + Math.cos(startAngle) * this.R2, this.Y2 - this.dY2 + Math.sin(startAngle) * this.R2);

            for (let i = startAngle; i <= endAngle; i += PI / 50) {
              ctx.lineTo(this.X + Math.cos(i) * this.R, this.Y - this.dY + Math.sin(i) * this.R);
            }

            ctx.lineTo(this.X2 + Math.cos(endAngle) * this.R2, this.Y2 - this.dY2 + Math.sin(endAngle) * this.R2);
            ctx.closePath();
            ctx.fill();
          } else if (this.L < 0.2) {
            // Draw mouth
            ctx.strokeStyle = 'rgb(150, 150, 150)';
            ctx.lineWidth = 3;

            for (let i = 1; i >= -1; i -= 2) {
              ctx.beginPath();
              if (this.first !== 1) {
                ctx.moveTo(this.X2 + i * Math.cos(PI / 6) * this.R2, this.Y2 - this.dY2 + Math.sin(PI / 6) * this.R2);
                ctx.lineTo(this.X + i * Math.cos(PI / 6) * this.R, this.Y - this.dY + Math.sin(PI / 6) * this.R);
              } else {
                ctx.moveTo(this.X + i * Math.cos(5 * PI / 6) * this.R, this.Y - this.dY + Math.sin(5 * PI / 6) * this.R);
                ctx.lineTo(this.X, this.Y - this.dY + this.R / 3);
              }
              ctx.stroke();
            }

            // Draw eyes
            if (g.eye === 0) {
              for (let i = 1; i >= -1; i -= 2) {
                ctx.fillStyle = 'rgb(0, 0, 0)';
                ctx.beginPath();
                ctx.arc(this.X + i * this.R * 0.9, this.Y - this.R * 0.15, size / 6, 0, 2 * PI);
                ctx.fill();

                ctx.fillStyle = 'rgb(250, 250, 250)';
                ctx.beginPath();
                ctx.arc(this.X + i * this.R * 0.93, this.Y - this.R * 0.18, size / 20, 0, 2 * PI);
                ctx.fill();
              }
              g.eye = 1;
            }
          }
        } else {
          // Draw tail
          for (let i = 1; i >= -1; i -= 2) {
            ctx.beginPath();
            ctx.moveTo(this.X + i * this.dX, this.Y - this.dY + this.R);
            ctx.lineTo(this.X2 + i * this.dX2, this.Y2 - this.dY2 + this.R2);
            ctx.lineTo(this.X2 + i * this.dX2, this.Y2 - this.dY2 - this.R2);
            ctx.lineTo(this.X + i * this.dX, this.Y - this.dY - this.R);
            ctx.closePath();
            ctx.fill();

            ctx.beginPath();
            ctx.moveTo(this.X + i * this.dX, this.Y - this.dY + this.R);
            ctx.lineTo(this.X + i * this.dX - this.tailR, this.Y - this.dY);
            ctx.lineTo(this.X + i * this.dX, this.Y - this.dY - this.R);
            ctx.lineTo(this.X + i * this.dX + this.tailR, this.Y - this.dY);
            ctx.closePath();
            ctx.fill();

            ctx.beginPath();
            ctx.moveTo(this.X + i * this.dX - this.tailR, this.Y - this.dY);
            ctx.lineTo(this.X2 + i * this.dX2 - this.tailR2, this.Y2 - this.dY2);
            ctx.lineTo(this.X2 + i * this.dX2 + this.tailR2, this.Y2 - this.dY2);
            ctx.lineTo(this.X + i * this.dX + this.tailR, this.Y - this.dY);
            ctx.closePath();
            ctx.fill();
          }
        }
      }
    }

    // Initialize layers
    if (layersRef.current.length === 0) {
      for (let i = numLayers - 1; i >= 0; i--) {
        layersRef.current.push(new Layer(i, canvas.width, canvas.height));
      }
      setMousePos({ x: canvas.width / 2, y: canvas.height / 2 });
    }

    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    // Animation loop
    let animationId;
    const animate = () => {
      globalVars.current.eye = 0;

      ctx.fillStyle = 'rgb(200, 230, 250)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update all layers
      for (let i = layersRef.current.length - 1; i >= 0; i--) {
        layersRef.current[i].updateLayer(mousePos.x, mousePos.y);
      }

      // Draw all layers
      for (let i = 0; i < layersRef.current.length; i++) {
        layersRef.current[i].drawLayer(ctx);
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [mousePos.x, mousePos.y]);

  return (
    <div className="w-full h-screen overflow-hidden bg-gradient-to-b from-blue-100 to-blue-200">
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-none"
      />
    </div>
  );
};

export default BlanketGhost;
